/**
 * @file useTreeNavigation.js
 * @description Tree navigation, selection, and expansion logic for BRICK Schema hierarchy
 *
 * Handles:
 * - Tree state preservation and restoration during refreshes
 * - Node selection and expansion
 * - Tree building from hierarchy data
 * - Node expansion/collapse operations
 */

import { ref, computed, nextTick } from 'vue';

export function useTreeNavigation() {
  // ============================================================================
  // STATE
  // ============================================================================

  /**
   * Currently expanded nodes in hierarchy tree
   * @type {Ref<Array<string>>}
   */
  const expandedHierarchyNodes = ref([]);

  /**
   * Currently expanded nodes in electrical tree
   * @type {Ref<Array<string>>}
   */
  const expandedElectricalNodes = ref([]);

  /**
   * Currently selected node in hierarchy tree
   * @type {Ref<Object|null>}
   */
  const selectedHierarchyNode = ref(null);

  /**
   * Currently selected node in electrical tree
   * @type {Ref<Object|null>}
   */
  const selectedElectricalNode = ref(null);

  /**
   * Flag indicating tree state is being preserved during refresh
   * @type {Ref<boolean>}
   */
  const preservingTreeState = ref(false);

  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================

  /**
   * Get type of currently selected hierarchy node
   */
  const selectedHierarchyNodeType = computed(() => {
    if (!selectedHierarchyNode.value) {return null;}
    return selectedHierarchyNode.value.type;
  });

  /**
   * Get type of currently selected electrical node
   */
  const selectedElectricalNodeType = computed(() => {
    if (!selectedElectricalNode.value) {return null;}
    return selectedElectricalNode.value.type;
  });

  // ============================================================================
  // TREE STATE PRESERVATION
  // ============================================================================

  /**
   * Preserve current tree expansion state before refresh
   * Captures expanded nodes and scroll position
   *
   * @returns {Object} State object with expandedNodes and scrollTop
   */
  function preserveHierarchyTreeState() {
    const treeContainer = document.querySelector('.hierarchy-tree-container');
    const currentExpanded = [...expandedHierarchyNodes.value];


    return {
      expandedNodes: currentExpanded,
      scrollTop: treeContainer?.scrollTop || 0,
    };
  }

  /**
   * Restore tree expansion state after refresh
   * Restores expanded nodes and scroll position
   *
   * @param {Object} state - State object from preserveHierarchyTreeState
   * @param {Array<string>} additionalExpanded - Additional nodes to expand (e.g., newly created)
   */
  async function restoreHierarchyTreeState(state, additionalExpanded = []) {

    // Restore expanded nodes
    expandedHierarchyNodes.value = state.expandedNodes;

    // Add any additional nodes to expand
    additionalExpanded.forEach((nodeId) => {
      if (!expandedHierarchyNodes.value.includes(nodeId)) {
        expandedHierarchyNodes.value.push(nodeId);
      }
    });


    // Wait for Vue to render
    await nextTick();

    // Restore scroll position
    const treeContainer = document.querySelector('.hierarchy-tree-container');
    if (treeContainer) {
      treeContainer.scrollTop = state.scrollTop;
    }
  }

  /**
   * Preserve electrical tree state
   */
  function preserveElectricalTreeState() {
    const treeContainer = document.querySelector('.electrical-tree-container');
    const currentExpanded = [...expandedElectricalNodes.value];


    return {
      expandedNodes: currentExpanded,
      scrollTop: treeContainer?.scrollTop || 0,
    };
  }

  /**
   * Restore electrical tree state
   */
  async function restoreElectricalTreeState(state, additionalExpanded = []) {

    expandedElectricalNodes.value = state.expandedNodes;

    additionalExpanded.forEach((nodeId) => {
      if (!expandedElectricalNodes.value.includes(nodeId)) {
        expandedElectricalNodes.value.push(nodeId);
      }
    });

    await nextTick();

    const treeContainer = document.querySelector('.electrical-tree-container');
    if (treeContainer) {
      treeContainer.scrollTop = state.scrollTop;
    }
  }

  // ============================================================================
  // NODE SELECTION
  // ============================================================================

  /**
   * Handle hierarchy node selection
   * @param {Object} node - Selected tree node
   */
  function onHierarchyNodeSelected(node) {
    selectedHierarchyNode.value = node;
  }

  /**
   * Handle electrical node selection
   * @param {Object} node - Selected tree node
   */
  function onElectricalNodeSelected(node) {
    selectedElectricalNode.value = node;
  }

  /**
   * Clear hierarchy node selection
   */
  function clearHierarchySelection() {
    selectedHierarchyNode.value = null;
  }

  /**
   * Clear electrical node selection
   */
  function clearElectricalSelection() {
    selectedElectricalNode.value = null;
  }

  // ============================================================================
  // NODE EXPANSION
  // ============================================================================

  /**
   * Expand all nodes in hierarchy tree
   * @param {Array<Object>} nodes - Tree nodes
   */
  function expandAllHierarchyNodes(nodes) {
    const nodeKeys = [];

    function collectKeys(nodeArray) {
      nodeArray.forEach((node) => {
        nodeKeys.push(node.id);
        if (node.children && node.children.length > 0) {
          collectKeys(node.children);
        }
      });
    }

    collectKeys(nodes);
    expandedHierarchyNodes.value = nodeKeys;

  }

  /**
   * Collapse all nodes in hierarchy tree
   */
  function collapseAllHierarchyNodes() {
    expandedHierarchyNodes.value = [];
  }

  /**
   * Expand all nodes in electrical tree
   * @param {Array<Object>} nodes - Tree nodes
   */
  function expandAllElectricalNodes(nodes) {
    const nodeKeys = [];

    function collectKeys(nodeArray) {
      nodeArray.forEach((node) => {
        nodeKeys.push(node.id);
        if (node.children && node.children.length > 0) {
          collectKeys(node.children);
        }
      });
    }

    collectKeys(nodes);
    expandedElectricalNodes.value = nodeKeys;

  }

  /**
   * Collapse all nodes in electrical tree
   */
  function collapseAllElectricalNodes() {
    expandedElectricalNodes.value = [];
  }

  /**
   * Expand node and all its parents (reveal path to node)
   * @param {Array<Object>} nodes - Tree nodes
   * @param {string} targetNodeId - Node ID to reveal
   * @param {string} treeType - 'hierarchy' or 'electrical'
   */
  function expandNodePath(nodes, targetNodeId, treeType = 'hierarchy') {
    const path = [];

    function findPath(nodeArray, parentPath = []) {
      for (const node of nodeArray) {
        const currentPath = [...parentPath, node.id];

        if (node.id === targetNodeId) {
          path.push(...currentPath);
          return true;
        }

        if (node.children && node.children.length > 0) {
          if (findPath(node.children, currentPath)) {
            return true;
          }
        }
      }
      return false;
    }

    findPath(nodes);

    if (path.length > 0) {
      if (treeType === 'hierarchy') {
        path.forEach((nodeId) => {
          if (!expandedHierarchyNodes.value.includes(nodeId)) {
            expandedHierarchyNodes.value.push(nodeId);
          }
        });
      } else {
        path.forEach((nodeId) => {
          if (!expandedElectricalNodes.value.includes(nodeId)) {
            expandedElectricalNodes.value.push(nodeId);
          }
        });
      }

    }
  }

  // ============================================================================
  // NODE SEARCH
  // ============================================================================

  /**
   * Find node in tree by ID
   * @param {Array<Object>} nodes - Tree nodes
   * @param {string} nodeId - Node ID to find
   * @returns {Object|null} Found node or null
   */
  function findNodeInTree(nodes, nodeId) {
    for (const node of nodes) {
      if (node.id === nodeId) {
        return node;
      }

      if (node.children && node.children.length > 0) {
        const found = findNodeInTree(node.children, nodeId);
        if (found) {
          return found;
        }
      }
    }

    return null;
  }

  /**
   * Get path to node (array of parent IDs)
   * @param {Array<Object>} nodes - Tree nodes
   * @param {string} targetNodeId - Node ID
   * @returns {Array<string>} Path of node IDs
   */
  function getNodePath(nodes, targetNodeId) {
    const path = [];

    function findPath(nodeArray, currentPath = []) {
      for (const node of nodeArray) {
        const newPath = [...currentPath, node.id];

        if (node.id === targetNodeId) {
          path.push(...newPath);
          return true;
        }

        if (node.children && node.children.length > 0) {
          if (findPath(node.children, newPath)) {
            return true;
          }
        }
      }
      return false;
    }

    findPath(nodes);
    return path;
  }

  // ============================================================================
  // RETURN API
  // ============================================================================

  return {
    // State
    expandedHierarchyNodes,
    expandedElectricalNodes,
    selectedHierarchyNode,
    selectedElectricalNode,
    preservingTreeState,

    // Computed
    selectedHierarchyNodeType,
    selectedElectricalNodeType,

    // Tree state preservation
    preserveHierarchyTreeState,
    restoreHierarchyTreeState,
    preserveElectricalTreeState,
    restoreElectricalTreeState,

    // Node selection
    onHierarchyNodeSelected,
    onElectricalNodeSelected,
    clearHierarchySelection,
    clearElectricalSelection,

    // Node expansion
    expandAllHierarchyNodes,
    collapseAllHierarchyNodes,
    expandAllElectricalNodes,
    collapseAllElectricalNodes,
    expandNodePath,

    // Node search
    findNodeInTree,
    getNodePath,
  };
}
